import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Label, Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@clinic-os/ui';
import { Stethoscope, Loader2, AlertCircle, Shield, Quote } from 'lucide-react';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const { error: authError } = await signIn(email, password);
      if (authError) {
        setError(authError.message);
      } else {
        navigate(from, { replace: true });
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen">
      {/* Left panel — testimonial + trust (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden flex-col justify-between p-12">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="login-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#login-grid)" />
          </svg>
        </div>

        {/* Brand */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-white">MedFlow</span>
        </div>

        {/* Testimonial */}
        <div className="relative space-y-6">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
            <Quote className="w-5 h-5 text-white/60" />
          </div>
          <blockquote className="text-xl text-white leading-relaxed">
            "I used to spend 45 minutes on paperwork after every shift. With MedFlow AI notes, I'm done in 5. That's time I give back to my patients."
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold">
              DL
            </div>
            <div>
              <p className="text-white font-medium">Dr. Lisa Chen</p>
              <p className="text-blue-50 text-sm">Family Practice, Singapore</p>
            </div>
          </div>
        </div>

        {/* Trust signals */}
        <div className="relative flex items-center gap-2 text-white">
          <Shield className="w-4 h-4 text-blue-50" />
          <span className="text-sm">HIPAA-compliant · End-to-end encrypted · SOC 2</span>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-600">
              <Stethoscope className="h-7 w-7 text-white" />
            </div>
            <p className="mt-4 text-2xl font-bold text-slate-900">MedFlow</p>
          </div>

          {/* Login Card */}
          <Card>
            <CardHeader>
              <CardTitle as="h1" className="text-xl" data-testid="login-title">Sign in to your account</CardTitle>
              <CardDescription>
                Enter your credentials to access the dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="login-form"
                data-appdom-id="login-form"
                data-appdom-type="Form"
                data-appdom-scope="onboarding"
              >
                {error && (
                  <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="auth-error">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="email-input"
                    data-appdom-id="login-email"
                    data-appdom-type="Input"
                    data-appdom-scope="onboarding"
                    placeholder="doctor@clinic.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="password-input"
                    data-appdom-id="login-password"
                    data-appdom-type="Input"
                    data-appdom-scope="onboarding"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                    data-testid="login-submit"
                  data-appdom-id="login-submit-cta"
                  data-appdom-type="Button"
                  data-appdom-role="primary"
                  data-appdom-scope="onboarding"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or</span>
                </div>
              </div>

              <p className="text-center text-sm text-slate-600">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="font-medium text-blue-600 hover:text-blue-700"
                  data-testid="register-link"
                >
                  Sign up
                </Link>
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </main>
  );
}
